import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import mic from "../../assets/icons/mic.png";
import RadicalTrashIcon from "./components/RadicalTrashIcon";
import TemplatesSection from "./components/TemplatesSection";
import MacrosSection from "./components/MacrosSection";
import {
  useTemplates,
  useMacros,
  appendReportEngineDictionaryPhrases,
} from "../../utils/API_SERVICE";
import { useSidebarDictation } from "./hooks/useSidebarDictation";
import { useCreDictationMirror } from "./hooks/useCreDictationMirror";
import { useNewPatientReport } from "./hooks/useNewPatientReport";
import {
  useCreMirrorFeedback,
  useCreMirrorLabels,
  useCreMirrorPayloadExtension,
  useCrePageAuth,
} from "./hooks/cre";
import SidebarMicPopover from "./components/SidebarMicPopover";
import DictationPhrasesSettingsMenu from "./components/DictationPhrasesSettingsMenu";
import MicTargetLetterBadge from "./components/MicTargetLetterBadge";
import CreExtensionHelpModal from "./components/CreExtensionHelpModal";
import PhilipsDeviceConnectorHelpModal from "./components/PhilipsDeviceConnectorHelpModal";
import usePhilipsPdcSidebarIcon from "./hooks/usePhilipsPdcSidebarIcon";
import { useGlobalStore } from "../../context/UserContext/userContext";
import { usePatientAuthFlags } from "../../Hooks/usePatientAuthFlags";
import { isVoiceTestAllowedForUser } from "../../utils/allowlists";
import LanguageDropdown from "../../components/common/LanguageDropdown";
import { DICTATION_LANGUAGES } from "../../constants/languages";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  TbPictureInPicture,
  TbPictureInPictureOff,
  TbTerminal2,
  TbMicrophone2,
} from "react-icons/tb";
import { FaMagic, FaMicrophone } from "react-icons/fa";
import ModelSpeedSettingsPopover from "../../components/common/ModelSpeedSettingsPopover";
import { useI18nLanguage } from "../../i18n/hooks/useI18nLanguage";
import {
  getRadicalMicButtonClass,
  RADICAL_COMPACT_MIC_ICON_CLASS,
  RADICAL_FINDINGS_BTN_SIZE_CLASS,
  RADICAL_FINDINGS_ICON_SIZE_CLASS,
  RADICAL_SIDEBAR_MIC_ICON_CLASS,
} from "./components/radicalMicButtonStyles";
import { getSidebarMicUiState } from "./utils/sidebarMicUi";
import { MIC_TOGGLE_TARGET } from "./utils/micStartTarget";
const MOBILE_BREAKPOINT_PX = 1024;

function getMobileDisplaySections(sec) {
  const result = [];
  const seen = new Set();
  for (const s of sec) {
    const key = s === "templates" || s === "macros" ? "template&macro" : s;
    if ((key === "reportAudio" || key === "template&macro") && !seen.has(key)) {
      result.push(key);
      seen.add(key);
    }
  }
  return result.length ? result : ["reportAudio", "template&macro"];
}

function getDesktopDisplaySections(sec) {
  return sec.flatMap((s) =>
    s === "template&macro" ? ["templates", "macros"] : [s],
  );
}

const DEFAULT_SECTIONS = ["reportAudio", "templates", "macros"];

const DRAG_HANDLE_CLASS =
  "flex absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[20%] w-8 h-8 bg-white dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-700 rounded-full items-center justify-center cursor-grab active:cursor-grabbing z-10 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-colors touch-manipulation";

const DRAG_HANDLE_SVG = (
  <svg
    className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300 shrink-0"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
);

function DraggableSection({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Don't block touch events on container - only on drag handle
    touchAction: "auto",
    cursor: "default",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children(listeners)}
    </div>
  );
}

function PatientSidebar({
  language = "en",
  onBeforeStartRecording,
  registerStopMicHandler,
  registerStartMicHandler,
  registerClearReportHandler,
  onDeleteTranscriptionRequest,
  findingsTextareaRef,
  micToggleTarget = MIC_TOGGLE_TARGET.FINDINGS,
  onSidebarFocus,
  onSidebarRecordingChange,
  onSidebarMicClick,
  isAnyMicActive = false,
  onTemplateSelect,
  onTemplateAppend,
  onTemplatesReady,
  onMacrosReady,
  selectedTemplateId = null,
  onSelectedTemplateIdChange = () => {},
  relatedUserId,
  onMacroSelect,
  sidebarTranscript,
  setSidebarTranscript,
  anathomy = "",
  dictationLanguage = "en-US",
  setDictationLanguage,
  anathomies = [],
  onProcessWithAI,
  isAiLoading = false,
  onVoiceMacroResult = () => {},
  modelSpeed = "Smarter",
  setModelSpeed,
  findingsOnly = false,
  onFindingsOnlyChange,
}) {
  const location = useLocation();
  const { t } = useTranslation("common");
  const { isRTL } = useI18nLanguage();
  const mirrorFeedback = useCreMirrorFeedback();
  const {
    notifySuccess: notifyCreMirrorSuccess,
    notifyWarning: notifyCreMirrorWarning,
    notifyError: notifyCreMirrorError,
  } = mirrorFeedback;
  const {
    isWixAuth,
    isCreAuth,
    isRadicalAuth,
    wixUser,
  } = usePatientAuthFlags();

  /** Sidebar mic: command-only (no plain dictation append). */
  const [macroCommandMode, setMacroCommandMode] = useState(false);
  const internalFindingsTextareaRef = useRef(null);
  const transcriptTextareaRef =
    findingsTextareaRef ?? internalFindingsTextareaRef;

  const [templates, setTemplates] = useState([]);
  const [macros, setMacros] = useState([]);
  const [selectedMacroId, setSelectedMacroId] = useState("");
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [isMobileView, setIsMobileView] = useState(() =>
    typeof window !== "undefined"
      ? window.innerWidth < MOBILE_BREAKPOINT_PX
      : false,
  );
  const [draggingEnabled] = useState(true);

  const dictation = useSidebarDictation({
    sidebarTranscript,
    setSidebarTranscript,
    transcriptTextareaRef,
    dictationLanguage,
    anathomy,
    macros,
    templates,
    macroCommandMode,
    setMacroCommandMode,
    onBeforeStartRecording,
    onSelectedTemplateIdChange,
    onTemplateSelect,
    onVoiceMacroResult,
    notifyCreMirrorSuccess,
    notifyCreMirrorWarning,
    t,
  });

  const {
    streamingTranscript,
    isRecording,
    recordingTime,
    handleSidebarTranscriptChange,
    handleDictationLanguageChange: emitDictationLanguage,
    handleCommandModeButtonClick,
    handleSidebarMicPopoverStop,
    toggleRecording,
    clearStreamingTranscript,
    resetRecordingTimerDisplay,
    clearSidebarReportState,
  } = dictation;

  const {
    buttonActive: sidebarMicButtonActive,
    dictationActive: sidebarDictationActive,
  } = getSidebarMicUiState({ isRecording, isAnyMicActive });
  const sidebarMicButtonLabel = sidebarMicButtonActive
    ? t("stopRecording", { defaultValue: "Stop recording" })
    : t("startRecording", { defaultValue: "Start recording" });

  const handleDictationLanguageChange = (selectedLanguage) => {
    setDictationLanguage(selectedLanguage);
    emitDictationLanguage(selectedLanguage);
  };

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const update = () => setIsMobileView(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  const displaySections = useMemo(
    () =>
      isMobileView
        ? getMobileDisplaySections(sections)
        : getDesktopDisplaySections(sections),
    [isMobileView, sections],
  );

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMacroSearchOpen, setIsMacroSearchOpen] = useState(false);
  const [macroSearchQuery, setMacroSearchQuery] = useState("");
  const [creExtensionHelpOpen, setCreExtensionHelpOpen] = useState(false);
  const [philipsPdcHelpOpen, setPhilipsPdcHelpOpen] = useState(false);
  const {
    philipsPdc,
    iconTitle: philipsPdcIconTitle,
    iconButtonClassName: philipsPdcIconButtonClassName,
    showIcon: showPhilipsPdcIcon,
    showDeviceReadyDot: showPhilipsPdcDeviceReadyDot,
  } = usePhilipsPdcSidebarIcon();

  const creUser = useGlobalStore((s) => s.creUser);
  const radicalUser = useGlobalStore((s) => s.radicalUser);
  const getAccessToken = useGlobalStore((s) => s.getAccessToken);
  const navigate = useNavigate();
  const isVoiceTestAllowed = isVoiceTestAllowedForUser({
    isWixAuth,
    isCreAuth,
    isRadicalAuth,
    wixUser,
    creUser,
    radicalUser,
  });

  const getAnathomyNames = useCallback(
    (anathomyCode) => {
      if (!anathomyCode || String(anathomyCode).trim() === "") return "";
      const code = String(anathomyCode).trim();
      const normalized = code.toLowerCase();
      const found = anathomies.find(
        (item) =>
          String(item?.code || "")
            .trim()
            .toLowerCase() === normalized,
      );
      return found ? found.name : "";
    },
    [anathomies],
  );

  const handleAddCustomPhrases = useCallback(
    async (phrases) => {
      try {
        const token = await getAccessToken();
        if (!token || !String(token).trim()) {
          toast.error(t("manageCustomPhrasesNoToken"));
          return;
        }

        const languageShort = (dictationLanguage || "en")
          .split("-")[0]
          .toLowerCase();

        if (!selectedTemplateId) {
          toast.error(t("manageCustomPhrasesSelectTemplateFirst"));
          return;
        }

        const selectedTemplate = templates.find(
          (tpl) => tpl?.templateId === selectedTemplateId,
        );
        const anatomyCode =
          selectedTemplate?.anathomy && String(selectedTemplate.anathomy).trim()
            ? String(selectedTemplate.anathomy).trim()
            : "";

        if (!anatomyCode) {
          toast.error(t("manageCustomPhrasesTemplateMissingAnatomy"));
          return;
        }

        const res = await appendReportEngineDictionaryPhrases({
          providerToken: token,
          language: languageShort,
          anatomyCode,
          phrases,
        });

        const backendMessage = res?.message ? String(res.message).trim() : "";
        toast.success(
          backendMessage ||
            t("manageCustomPhrasesAdded", {
              added: res?.added ?? phrases.length,
            }),
        );
      } catch (err) {
        console.error("Add custom phrases:", err);
        const detail =
          err?.message && String(err.message).trim()
            ? String(err.message).trim()
            : "";
        toast.error(detail || t("manageCustomPhrasesFailed"));
      }
    },
    [dictationLanguage, getAccessToken, selectedTemplateId, t, templates],
  );

  const resizeTranscriptToMaxRows = useCallback(() => {
    const el = transcriptTextareaRef.current;
    if (!el) return;

    // Start small (original mins); grow with content up to a higher max.
    const MIN_HEIGHT_PX = isRadicalAuth ? 120 : 40;
    const MAX_HEIGHT_PX = isRadicalAuth ? 520 : 280;

    el.style.minHeight = `${MIN_HEIGHT_PX}px`;
    el.style.maxHeight = `${MAX_HEIGHT_PX}px`;
    // Collapse first so scrollHeight reflects full content (height:"auto" is unreliable on textareas).
    el.style.height = "0px";
    const contentHeight = el.scrollHeight;
    const nextHeight = Math.min(
      Math.max(contentHeight, MIN_HEIGHT_PX),
      MAX_HEIGHT_PX,
    );
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = contentHeight > MAX_HEIGHT_PX ? "auto" : "hidden";
  }, [transcriptTextareaRef, isRadicalAuth]);

  useLayoutEffect(() => {
    resizeTranscriptToMaxRows();
  }, [sidebarTranscript, resizeTranscriptToMaxRows]);

  const isRecordingRef = useRef(isRecording);
  isRecordingRef.current = isRecording;

  useEffect(() => {
    registerStartMicHandler?.(async () => {
      if (isRecordingRef.current) return;
      await toggleRecording();
    });
    return () => {
      registerStartMicHandler?.(null);
    };
  }, [toggleRecording, registerStartMicHandler]);

  useEffect(() => {
    onSidebarRecordingChange?.(isRecording);
  }, [isRecording, onSidebarRecordingChange]);

  useEffect(() => {
    registerStopMicHandler?.(async () => {
      if (!isRecordingRef.current) return;
      await toggleRecording();
    });
    return () => {
      registerStopMicHandler?.(null);
    };
  }, [toggleRecording, registerStopMicHandler]);

  useEffect(() => {
    registerClearReportHandler?.(() => {
      clearSidebarReportState();
    });
    return () => {
      registerClearReportHandler?.(null);
    };
  }, [clearSidebarReportState, registerClearReportHandler]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")} min`;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: draggingEnabled ? 8 : Infinity,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDeleteTranscript = useCallback(() => {
    // Use the hook's handler so Soniox (he/fr) cumulative state resets too.
    handleSidebarTranscriptChange("");
    clearStreamingTranscript();
    if (!isRecording) {
      resetRecordingTimerDisplay();
    }
  }, [
    handleSidebarTranscriptChange,
    clearStreamingTranscript,
    isRecording,
    resetRecordingTimerDisplay,
  ]);

  const {
    data: templatesData,
    isLoading: isTemplatesLoading,
    refetch: refetchTemplates,
  } = useTemplates({
    ...(relatedUserId ? { relatedUserId } : {}),
    // Radical: templates are English only for now.
    language: isWixAuth || isCreAuth ? language || null : "en",
    // TODO: UNCOMMENT THIS WHEN WE HAVE BETTER TEMPLATES
  });

  const {
    data: macrosData,
    // isLoading: macrosLoading,
    // error: macrosError,
    refetch: refetchMacros,
  } = useMacros({
    relatedUserId: relatedUserId,
  });

  useEffect(() => {
    refetchTemplates();
    refetchMacros();
  }, [
    location.key,
    language,
    isWixAuth,
    isCreAuth,
    refetchTemplates,
    refetchMacros,
  ]);

  useEffect(() => {
    if (templatesData && !isTemplatesLoading) {
      setTemplates(templatesData);
      onTemplatesReady?.(templatesData);
      if (
        selectedTemplateId &&
        !templatesData.some(
          (template) => template.templateId === selectedTemplateId,
        )
      ) {
        onSelectedTemplateIdChange(null);
      }
    }
  }, [
    templatesData,
    isTemplatesLoading,
    onTemplatesReady,
    selectedTemplateId,
    onSelectedTemplateIdChange,
  ]);

  useEffect(() => {
    if (macrosData) {
      setMacros(macrosData);
      onMacrosReady?.(macrosData);
    }
  }, [macrosData, onMacrosReady]);

  /* CRE Document PiP: labels + optional payload → useCreDictationMirror / extension */
  const mirrorLabels = useCreMirrorLabels();
  const patientMirrorExt = useCreMirrorPayloadExtension();
  const pageAuth = useCrePageAuth();
  const {
    startNewPatientReport,
    isResetting: newPatientBusy,
  } = useNewPatientReport();

  const templateMirrorPayload = useMemo(
    () => ({
      templates: templates.map((template) => {
        const anatomyCode =
          template?.anathomy && String(template.anathomy).trim()
            ? String(template.anathomy).trim()
            : "";
        const anatomyName = anatomyCode ? getAnathomyNames(anatomyCode) : "";
        const label = String(template.templateName ?? "");
        return {
          value: String(template.templateId ?? ""),
          label,
          subLabel: anatomyName ? `Anatomy: ${anatomyName}` : "\u00a0",
          searchText: [label, anatomyName].filter(Boolean).join(" "),
        };
      }),
      selectedTemplateId:
        selectedTemplateId != null ? String(selectedTemplateId) : "",
    }),
    [templates, selectedTemplateId, getAnathomyNames],
  );

  const mirrorPayloadExtension = useMemo(
    () => ({
      ...patientMirrorExt,
      ...mirrorFeedback.payloadFields,
      processAiBusy: Boolean(isAiLoading),
      macroCommandMode: Boolean(macroCommandMode),
      ...templateMirrorPayload,
      newPatientBusy: Boolean(newPatientBusy),
      pageAuth,
    }),
    [
      patientMirrorExt,
      mirrorFeedback.payloadFields,
      isAiLoading,
      macroCommandMode,
      templateMirrorPayload,
      newPatientBusy,
      pageAuth,
    ],
  );

  const mirrorOnProcessWithAIRef = useRef(async () => {});
  const mirrorOnTemplateSelectRef = useRef((_templateId) => {});
  const mirrorOnNewPatientRef = useRef(async () => {});
  const flushMirrorStateRef = useRef(() => {});

  const {
    extensionPresence: creExtensionPresence,
    mirrorSessionActive,
    openMirror,
    closeMirror,
    notifyMainTranscriptFocus,
    flushMirrorState,
  } = useCreDictationMirror({
    transcriptTextareaRef,
    sidebarTranscript,
    setSidebarTranscript,
    streamingTranscript,
    isRecording,
    recordingTime,
    dictationLanguage,
    setDictationLanguage,
    languages: DICTATION_LANGUAGES,
    toggleRecording,
    handleDeleteTranscript,
    mirrorLabels,
    mirrorPayloadExtension,
    onProcessWithAI: () => mirrorOnProcessWithAIRef.current(),
    onMacroCommandModeToggle: handleCommandModeButtonClick,
    onTemplateSelect: (templateId) => {
      mirrorOnTemplateSelectRef.current(templateId);
    },
    onNewPatient: () => mirrorOnNewPatientRef.current(),
    pageAuth,
  });

  flushMirrorStateRef.current = flushMirrorState;

  mirrorOnProcessWithAIRef.current = async () => {
    if (!onProcessWithAI) return;
    try {
      const result = await onProcessWithAI();
      if (result?.ok) {
        queueMicrotask(() => flushMirrorStateRef.current());
      }
      if (!result || typeof result !== "object" || !("ok" in result)) return;
      const msg =
        typeof result.mirrorMessage === "string"
          ? result.mirrorMessage.trim()
          : "";
      if (!msg) return;
      if (result.ok) {
        notifyCreMirrorSuccess(msg);
      } else if (result.mirrorLevel === "warning") {
        notifyCreMirrorWarning(msg);
      } else {
        notifyCreMirrorError(msg);
      }
    } catch {
      notifyCreMirrorError(t("aiProcessingFailed"));
    }
  };

  // Anatomies are now received as prop from Patient.jsx (single API call)

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const current = isMobileView
        ? getMobileDisplaySections(sections)
        : getDesktopDisplaySections(sections);
      const oldIndex = current.indexOf(active.id);
      const newIndex = current.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;
      setSections(arrayMove(current, oldIndex, newIndex));
    },
    [isMobileView, sections],
  );

  const handleSearchToggle = () => {
    setIsSearchOpen(!isSearchOpen);
    if (isSearchOpen) {
      setSearchQuery("");
    }
  };

  const handleMacroSearchToggle = () => {
    setIsMacroSearchOpen(!isMacroSearchOpen);
    if (isMacroSearchOpen) {
      setMacroSearchQuery("");
    }
  };

  const templateOptions = templates.map((template) => ({
    value: template.templateId,
    label: template.templateName,
  }));

  const macroOptions = macros.map((macro) => ({
    value: macro.macroId,
    label: macro.macroName,
  }));

  const handleTemplateDropdownChange = (templateId) => {
    const id = String(templateId ?? "");
    if (!id) return;
    const selectedTemplate = templates.find(
      (template) => String(template.templateId) === id,
    );
    if (selectedTemplate) {
      onSelectedTemplateIdChange(selectedTemplate.templateId);
      onTemplateSelect(selectedTemplate);
      queueMicrotask(() => flushMirrorStateRef.current());
    }
  };

  mirrorOnTemplateSelectRef.current = handleTemplateDropdownChange;
  mirrorOnNewPatientRef.current = startNewPatientReport;

  const handleMacroDropdownChange = (macroId) => {
    const selectedMacro = macros.find((macro) => macro.macroId === macroId);
    if (selectedMacro) {
      onMacroSelect(selectedMacro);
    }
    setSelectedMacroId("");
  };

  const handleSidebarTextareaFocus = useCallback(() => {
    notifyMainTranscriptFocus();
    onSidebarFocus?.();
  }, [notifyMainTranscriptFocus, onSidebarFocus]);

  const transcriptTextareaClassName = isRadicalAuth
    ? "w-full overflow-y-auto p-1 sm:p-2 resize-none bg-transparent border-none focus:outline-none text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 text-sm sm:text-base"
    : "w-full overflow-y-auto p-1 sm:p-2 resize-none bg-transparent border-none focus:outline-none text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 text-sm sm:text-base";

  const templateSectionProps = {
    templates,
    currentTemplateId: selectedTemplateId,
    setCurrentTemplateId: onSelectedTemplateIdChange,
    onTemplateSelect,
    onTemplateAppend,
    isSearchOpen,
    onSearchToggle: handleSearchToggle,
    searchQuery,
    onSearchQueryChange: setSearchQuery,
    templateOptions,
    onTemplateDropdownChange: handleTemplateDropdownChange,
    getAnathomyNames,
    t,
  };

  const macroSectionProps = {
    macros,
    selectedMacroId,
    onMacroSelect,
    isMacroSearchOpen,
    onMacroSearchToggle: handleMacroSearchToggle,
    macroSearchQuery,
    onMacroSearchQueryChange: setMacroSearchQuery,
    macroOptions,
    onMacroDropdownChange: handleMacroDropdownChange,
    t,
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      autoScroll={false}
    >
      <SortableContext items={displaySections}>
        <div className="space-y-1 sm:space-y-2" style={{ touchAction: "auto" }}>
          {displaySections.map((section) => {
            switch (section) {
              case "reportAudio":
                return (
                  <DraggableSection key={section} id={section}>
                    {(listeners) => (
                      <div
                        className={`relative bg-white dark:bg-gray-800 rounded-2xl ${
                          isRadicalAuth
                            ? "p-1.5 sm:p-2 flex flex-col min-h-0 flex-1"
                            : "p-2 sm:p-3"
                        }`}
                      >
                        <div
                          className={DRAG_HANDLE_CLASS}
                          {...listeners}
                          style={{ touchAction: "none" }}
                          title={t("dragToReorder") || "Drag to reorder"}
                          role="button"
                          aria-label={t("dragToReorder") || "Drag to reorder"}
                        >
                          {DRAG_HANDLE_SVG}
                        </div>
                        {isRadicalAuth ? (
                          <>
                            <div className="flex items-center justify-between gap-2 mb-2 min-w-0 border-b border-gray-200 pb-2 dark:border-gray-700">
                              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                                <div
                                  dir="ltr"
                                  className="inline-flex h-7 shrink-0 items-center justify-center rounded-full bg-[#EAEAEA] px-2.5 dark:bg-gray-700 sm:h-8"
                                >
                                  <span className="text-xs font-semibold tabular-nums text-gray-900 dark:text-gray-200">
                                    {formatTime(recordingTime)}
                                  </span>
                                </div>
                                <DictationPhrasesSettingsMenu
                                  title={t("manageCustomPhrasesPopoverTitle")}
                                  description={t(
                                    "manageCustomPhrasesPopoverDescription",
                                  )}
                                  inputLabel={t(
                                    "manageCustomPhrasesInputLabel",
                                  )}
                                  inputPlaceholder={t(
                                    "manageCustomPhrasesInputPlaceholder",
                                  )}
                                  addButtonLabel={t("manageCustomPhrases")}
                                  onAddPhrases={handleAddCustomPhrases}
                                  settingsAriaLabel={t(
                                    "manageCustomPhrasesSettingsAria",
                                  )}
                                  successText={t("manageCustomPhrasesHelp")}
                                />
                                <div className="relative shrink-0">
                                  <button
                                    type="button"
                                    data-dictation-mic-trigger="sidebar"
                                    className={getRadicalMicButtonClass(
                                      sidebarMicButtonActive,
                                      "compact",
                                    )}
                                    onClick={() => void onSidebarMicClick?.()}
                                    title={sidebarMicButtonLabel}
                                    aria-label={sidebarMicButtonLabel}
                                  >
                                    <FaMicrophone
                                      className={RADICAL_COMPACT_MIC_ICON_CLASS}
                                      aria-hidden
                                    />
                                  </button>
                                  <MicTargetLetterBadge
                                    toggleTarget={micToggleTarget}
                                    compact
                                    repositionByTarget
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 sm:gap-2">
                                <button
                                  type="button"
                                  className={`${RADICAL_FINDINGS_BTN_SIZE_CLASS} cursor-pointer rounded-full border border-gray-900/10 bg-[#CBEA7B80] text-gray-900 shadow-sm transition-colors hover:bg-[#A4C639] dark:border-gray-700 dark:text-black dark:hover:bg-[#CBEA7B]`}
                                  aria-label={t("processReportButton")}
                                  title={t("processReportButton")}
                                  onClick={() => void onProcessWithAI?.()}
                                >
                                  <FaMagic
                                    className={`${RADICAL_FINDINGS_ICON_SIZE_CLASS} shrink-0 text-gray-900 dark:text-black`}
                                  />
                                </button>
                                <ModelSpeedSettingsPopover
                                  compact
                                  value={modelSpeed}
                                  onChange={setModelSpeed}
                                  disabled={!setModelSpeed}
                                  isRTL={isRTL}
                                  groupLabel={t("modelSpeed")}
                                  groupHint={t("modelSpeedHint")}
                                  segmentSmarterLabel={t(
                                    "modelSpeedSegmentSmarter",
                                  )}
                                  segmentFastestLabel={t(
                                    "modelSpeedSegmentFastest",
                                  )}
                                  smarterTitle={t("modelSpeedSmarter")}
                                  fastestTitle={t("modelSpeedFastest")}
                                  settingsButtonTitle={t("findingsOnly")}
                                  findingsOnly={findingsOnly}
                                  onFindingsOnlyChange={onFindingsOnlyChange}
                                  findingsOnlyLabel={t("findingsOnly")}
                                  findingsOnlyHint={t("findingsOnlyHint")}
                                />
                                <button
                                  type="button"
                                  className={`${RADICAL_FINDINGS_BTN_SIZE_CLASS} rounded-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600`}
                                  onClick={() =>
                                    onDeleteTranscriptionRequest?.(
                                      handleDeleteTranscript,
                                    )
                                  }
                                  title={t("radicalClearTranscription")}
                                  aria-label={t("radicalClearTranscription")}
                                >
                                  <RadicalTrashIcon
                                    className={`${RADICAL_FINDINGS_ICON_SIZE_CLASS} text-red-600 dark:text-red-500`}
                                  />
                                </button>
                              </div>
                            </div>
                            <SidebarMicPopover
                              isRecording={sidebarDictationActive}
                              macroCommandMode={macroCommandMode}
                              dictationLanguage={dictationLanguage}
                              streamingTranscript={streamingTranscript}
                              onStop={handleSidebarMicPopoverStop}
                              isRadicalAuth={isRadicalAuth}
                            />
                            <textarea
                              ref={transcriptTextareaRef}
                              value={sidebarTranscript}
                              onFocus={handleSidebarTextareaFocus}
                              onChange={(e) =>
                                handleSidebarTranscriptChange(e.target.value)
                              }
                              onInput={resizeTranscriptToMaxRows}
                              dir={
                                dictationLanguage?.startsWith("he") ||
                                dictationLanguage?.startsWith("ar")
                                  ? "rtl"
                                  : "ltr"
                              }
                              style={{
                                textAlign:
                                  dictationLanguage?.startsWith("he") ||
                                  dictationLanguage?.startsWith("ar")
                                    ? "right"
                                    : "left",
                              }}
                              className={transcriptTextareaClassName}
                              placeholder={t("findingsPlaceholder")}
                              rows={1}
                            />
                            {sidebarDictationActive &&
                              (streamingTranscript ? (
                                <p
                                  dir={
                                    dictationLanguage?.startsWith("he") ||
                                    dictationLanguage?.startsWith("ar")
                                      ? "rtl"
                                      : "ltr"
                                  }
                                  style={{
                                    textAlign:
                                      dictationLanguage?.startsWith("he") ||
                                      dictationLanguage?.startsWith("ar")
                                        ? "right"
                                        : "left",
                                  }}
                                  className="text-gray-900 dark:text-gray-200 italic mt-1 text-xs sm:text-sm break-words"
                                >
                                  {streamingTranscript}
                                </p>
                              ) : (
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="h-2 w-2 animate-pulse rounded-full bg-green dark:bg-green-dark" />
                                  <p className="text-gray-500 dark:text-gray-400 italic text-xs sm:text-sm animate-pulse">
                                    Listening...
                                  </p>
                                </div>
                              ))}
                          </>
                        ) : (
                          <>
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 justify-between border-b border-gray-200 pb-2 mb-2 dark:border-gray-700">
                              <div className="flex gap-2 flex-1 min-w-0">
                                <div className="w-10 h-10 sm:w-11 sm:h-11 flex-shrink-0">
                                  {isVoiceTestAllowed ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        navigate(
                                          `/voice-test${location.search}`,
                                        )
                                      }
                                      className="w-full h-full bg-gray-200 dark:bg-gray-700 rounded-full place-content-center justify-items-center flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 cursor-pointer"
                                      title="Voice test"
                                    >
                                      <img
                                        src={mic}
                                        alt="mic icon"
                                        className="w-4 h-4 sm:w-5 sm:h-5 dark:invert"
                                      />
                                    </button>
                                  ) : (
                                    <div className="w-full h-full bg-gray-200 dark:bg-gray-700 rounded-full place-content-center justify-items-center flex items-center justify-center">
                                      <img
                                        src={mic}
                                        alt="mic icon"
                                        className="w-4 h-4 sm:w-5 sm:h-5 dark:invert"
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h1 className="font-bold text-gray-900 dark:text-gray-200 text-sm sm:text-base">
                                    {t("reportAudio")}
                                  </h1>
                                  <h1 className="text-gray-light dark:text-gray-200 text-[10px] sm:text-xs ml-1 leading-3">
                                    {t("dictateYourFindingsHere")}
                                  </h1>
                                </div>
                              </div>
                              <div className="flex gap-1.5 sm:gap-2 items-center justify-end flex-shrink-0">
                                <LanguageDropdown
                                  value={dictationLanguage}
                                  onChange={handleDictationLanguageChange}
                                  languages={DICTATION_LANGUAGES}
                                  className="min-w-[80px] sm:min-w-[100px] max-w-[120px] sm:max-w-[200px] flex-shrink"
                                />
                                {creExtensionPresence !== "pending" && (
                                  <button
                                    type="button"
                                    title={
                                      creExtensionPresence === "absent"
                                        ? t(
                                            "creFloatingDictationExtensionHelpTitle",
                                          )
                                        : mirrorSessionActive
                                        ? t("creFloatingDictationCloseTitle")
                                        : t("creFloatingDictationTitle")
                                    }
                                    aria-label={
                                      creExtensionPresence === "absent"
                                        ? t(
                                            "creFloatingDictationExtensionHelpTitle",
                                          )
                                        : mirrorSessionActive
                                        ? t("creFloatingDictationCloseTitle")
                                        : t("creFloatingDictationTitle")
                                    }
                                    onClick={() => {
                                      if (creExtensionPresence === "absent") {
                                        setCreExtensionHelpOpen(true);
                                        return;
                                      }
                                      mirrorSessionActive
                                        ? void closeMirror()
                                        : void openMirror();
                                    }}
                                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full place-content-center justify-items-center flex items-center justify-center flex-shrink-0 transition-colors ${
                                      creExtensionPresence === "present" &&
                                      mirrorSessionActive
                                        ? "bg-green dark:bg-green-dark text-gray-900 shadow-md ring-2 ring-green-dark/40 dark:ring-green/50 ring-offset-2 ring-offset-white dark:ring-offset-gray-800 hover:bg-green-dark dark:hover:bg-green"
                                        : "text-gray-800 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
                                    }`}
                                  >
                                    {creExtensionPresence === "present" &&
                                    mirrorSessionActive ? (
                                      <TbPictureInPictureOff
                                        className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                                        aria-hidden
                                      />
                                    ) : (
                                      <TbPictureInPicture
                                        className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                                        aria-hidden
                                      />
                                    )}
                                    <span className="sr-only">
                                      {creExtensionPresence === "absent"
                                        ? t(
                                            "creFloatingDictationExtensionHelpTitle",
                                          )
                                        : mirrorSessionActive
                                        ? t("creFloatingDictationCloseTitle")
                                        : t("creFloatingDictationTitle")}
                                    </span>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 flex place-content-center justify-items-center"
                                  onClick={handleDeleteTranscript}
                                  title="Clear transcription"
                                  aria-label="Clear transcription"
                                >
                                  <RadicalTrashIcon className="h-3 w-3 text-red-600 dark:text-red-500 sm:h-4 sm:w-4" />
                                </button>
                              </div>
                            </div>
                            <div
                              className={`relative flex w-full items-center justify-center ${
                                isRadicalAuth
                                  ? "mb-3 mt-4 min-h-8 sm:min-h-9"
                                  : "mb-9 mt-12 min-h-[5.25rem]"
                              }`}
                            >
                              <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
                                <div className="relative">
                                  <button
                                    type="button"
                                    data-dictation-mic-trigger="sidebar"
                                    className={`cursor-pointer ${
                                      isRadicalAuth
                                        ? getRadicalMicButtonClass(
                                            sidebarMicButtonActive,
                                            "sidebar",
                                          )
                                        : `flex h-16 w-16 items-center justify-center rounded-full border-2 border-transparent text-center shadow-sm ${
                                            sidebarMicButtonActive
                                              ? "bg-green dark:bg-green-dark hover:bg-green-dark dark:hover:bg-green"
                                              : "bg-red-500 hover:bg-red-600 dark:hover:bg-red-600"
                                          }`
                                    }`}
                                    onClick={() => void onSidebarMicClick?.()}
                                    title={sidebarMicButtonLabel}
                                    aria-label={sidebarMicButtonLabel}
                                  >
                                    <FaMicrophone
                                      className={
                                        isRadicalAuth
                                          ? RADICAL_SIDEBAR_MIC_ICON_CLASS
                                          : `h-6 w-6 sm:h-7 sm:w-7 ${
                                              sidebarMicButtonActive
                                                ? "text-gray-900 dark:text-gray-100"
                                                : "text-white"
                                            }`
                                      }
                                      aria-hidden
                                    />
                                  </button>
                                  <MicTargetLetterBadge
                                    toggleTarget={micToggleTarget}
                                  />
                                </div>
                              </div>
                              <div
                                dir="ltr"
                                className="absolute left-0 top-1/2 z-20 flex max-w-[46%] -translate-y-1/2 items-center gap-2 sm:max-w-[50%]"
                              >
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={macroCommandMode}
                                  aria-label={t(
                                    "sidebarCommandModeToggleLabel",
                                  )}
                                  title={t("sidebarCommandModeToggleLabel")}
                                  onClick={() =>
                                    void handleCommandModeButtonClick()
                                  }
                                  className={`flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500/70 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-800 sm:h-9 sm:w-9 ${
                                    macroCommandMode
                                      ? "bg-green/80 text-gray-800 shadow-sm ring-1 ring-green-dark/25 ring-offset-1 ring-offset-white hover:bg-green/90 dark:bg-green-dark/70 dark:text-gray-200 dark:ring-green/30 dark:ring-offset-gray-800 dark:hover:bg-green-dark/85"
                                      : "bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                                  }`}
                                >
                                  <TbTerminal2
                                    className="h-[18px] w-[18px] sm:h-5 sm:w-5"
                                    aria-hidden
                                  />
                                </button>
                                {showPhilipsPdcIcon && (
                                  <button
                                    type="button"
                                    title={philipsPdcIconTitle}
                                    aria-label={philipsPdcIconTitle}
                                    onClick={() => setPhilipsPdcHelpOpen(true)}
                                    className={philipsPdcIconButtonClassName}
                                  >
                                    <TbMicrophone2
                                      className="h-[18px] w-[18px] sm:h-5 sm:w-5"
                                      aria-hidden
                                    />
                                    {showPhilipsPdcDeviceReadyDot ? (
                                      <span
                                        className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-600 ring-2 ring-white dark:ring-gray-800 animate-pulse"
                                        aria-hidden
                                      />
                                    ) : null}
                                  </button>
                                )}
                              </div>
                              {sidebarDictationActive && (
                                <div className="pointer-events-none absolute right-0 top-1/2 z-20 flex h-7 min-w-[3.75rem] -translate-y-1/2 items-center justify-center rounded-full bg-[#EAEAEA] px-2 dark:bg-gray-700 sm:min-w-[4rem]">
                                  <span className="text-xs font-semibold tabular-nums text-gray-900 dark:text-gray-200">
                                    {formatTime(recordingTime)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <SidebarMicPopover
                              isRecording={sidebarDictationActive}
                              macroCommandMode={macroCommandMode}
                              dictationLanguage={dictationLanguage}
                              streamingTranscript={streamingTranscript}
                              onStop={handleSidebarMicPopoverStop}
                              isRadicalAuth={isRadicalAuth}
                            />
                            <div className="mt-3 sm:mt-4">
                              <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
                                <h2 className="font-bold text-gray-900 dark:text-gray-200 text-sm sm:text-base min-w-0">
                                  {t("sidebarTranscriptionHeading")}:
                                </h2>
                                <DictationPhrasesSettingsMenu
                                  title={t("manageCustomPhrasesPopoverTitle")}
                                  description={t(
                                    "manageCustomPhrasesPopoverDescription",
                                  )}
                                  inputLabel={t(
                                    "manageCustomPhrasesInputLabel",
                                  )}
                                  inputPlaceholder={t(
                                    "manageCustomPhrasesInputPlaceholder",
                                  )}
                                  addButtonLabel={t("manageCustomPhrases")}
                                  onAddPhrases={handleAddCustomPhrases}
                                  settingsAriaLabel={t(
                                    "manageCustomPhrasesSettingsAria",
                                  )}
                                  successText={t("manageCustomPhrasesHelp")}
                                />
                              </div>
                              <textarea
                                ref={transcriptTextareaRef}
                                value={sidebarTranscript}
                                onFocus={handleSidebarTextareaFocus}
                                onChange={(e) =>
                                  handleSidebarTranscriptChange(e.target.value)
                                }
                                onInput={resizeTranscriptToMaxRows}
                                dir={
                                  dictationLanguage?.startsWith("he") ||
                                  dictationLanguage?.startsWith("ar")
                                    ? "rtl"
                                    : "ltr"
                                }
                                style={{
                                  textAlign:
                                    dictationLanguage?.startsWith("he") ||
                                    dictationLanguage?.startsWith("ar")
                                      ? "right"
                                      : "left",
                                }}
                                className={transcriptTextareaClassName}
                                placeholder={t("findingsPlaceholder")}
                                rows={1}
                              />
                              {sidebarDictationActive &&
                                (streamingTranscript ? (
                                  <p
                                    dir={
                                      dictationLanguage?.startsWith("he") ||
                                      dictationLanguage?.startsWith("ar")
                                        ? "rtl"
                                        : "ltr"
                                    }
                                    style={{
                                      textAlign:
                                        dictationLanguage?.startsWith("he") ||
                                        dictationLanguage?.startsWith("ar")
                                          ? "right"
                                          : "left",
                                    }}
                                    className="text-gray-900 dark:text-gray-200 italic mt-1 text-xs sm:text-sm break-words"
                                  >
                                    {streamingTranscript}
                                  </p>
                                ) : (
                                  <div className="flex items-center gap-2 mt-1">
                                    <div className="h-2 w-2 animate-pulse rounded-full bg-green dark:bg-green-dark" />
                                    <p className="text-gray-500 dark:text-gray-400 italic text-xs sm:text-sm animate-pulse">
                                      Listening...
                                    </p>
                                  </div>
                                ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </DraggableSection>
                );
              case "templates":
                return (
                  <DraggableSection key={section} id={section}>
                    {(listeners) => (
                      <TemplatesSection
                        listeners={listeners}
                        {...templateSectionProps}
                      />
                    )}
                  </DraggableSection>
                );
              case "macros":
                return (
                  <DraggableSection key={section} id={section}>
                    {(listeners) => (
                      <MacrosSection
                        listeners={listeners}
                        {...macroSectionProps}
                      />
                    )}
                  </DraggableSection>
                );
              case "template&macro":
                return (
                  <DraggableSection key={section} id={section}>
                    {(listeners) => (
                      <div className="relative bg-white dark:bg-gray-800 rounded-2xl">
                        <div
                          className={DRAG_HANDLE_CLASS}
                          {...listeners}
                          style={{ touchAction: "none" }}
                          title={t("dragToReorder") || "Drag to reorder"}
                          role="button"
                          aria-label={t("dragToReorder") || "Drag to reorder"}
                        >
                          {DRAG_HANDLE_SVG}
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                          <TemplatesSection
                            listeners={{}}
                            hideDragHandle
                            {...templateSectionProps}
                          />
                          <MacrosSection
                            listeners={{}}
                            hideDragHandle
                            {...macroSectionProps}
                          />
                        </div>
                      </div>
                    )}
                  </DraggableSection>
                );
              default:
                return null;
            }
          })}
        </div>
      </SortableContext>
      <CreExtensionHelpModal
        isOpen={creExtensionHelpOpen}
        onClose={() => setCreExtensionHelpOpen(false)}
        alignBetweenTopAndCenter={isRadicalAuth}
      />
      <PhilipsDeviceConnectorHelpModal
        isOpen={philipsPdcHelpOpen}
        onClose={() => setPhilipsPdcHelpOpen(false)}
        connectionStatus={philipsPdc.status}
        deviceTypeName={philipsPdc.deviceTypeName}
        alignBetweenTopAndCenter={isRadicalAuth}
      />
    </DndContext>
  );
}

export default PatientSidebar;
